import React, { useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { renderToStaticMarkup } from "react-dom/server";
import ReactECharts from "echarts-for-react";
import HexagonTick from "app/assets/IconComponent/HexagonTick";
import FundingIcon from "app/assets/IconComponent/FundingIcon";
import WarningHex from "app/assets/IconComponent/WarningHex";
import TrueIqIcon from "app/assets/IconComponent/TrueIqIcon";
import { useNavigate } from "react-router-dom";

// ── Constants ─────────────────────────────────────────────────────────────────

type InvoiceStatus = "PAID" | "OVERDUE" | "GENERATED" | string;

const PRIMARY = "#1E3A8A";
const CURRENT_YEAR = new Date().getFullYear();
const INITIAL_VISIBLE_RECORDS = 10;

const PAYMENT_STATUS = {
  paid: { color: "#22C55E", label: "Paid" },
  due: { color: "#3B82F6", label: "Due" },
  overdue: { color: "#F97316", label: "Overdue" },
} as const;

type PaymentStatus = keyof typeof PAYMENT_STATUS;

const INVOICE_COLORS: Record<string, string> = {
  PAID: "#22C55E",
  OVERDUE: "#F97316",
  GENERATED: "#3B82F6",
};

const STATUS_BAR_COLORS: Record<string, { bg: string; border: string }> = {
  PAID: { bg: "#CAF1D8", border: "#76DB9B" },
  OVERDUE: { bg: "#FBA86F66", border: "#FBA86F" },
  GENERATED: { bg: "#1581ED40", border: "#1581ED" },
};

function invoiceColor(status: InvoiceStatus | null | undefined): string {
  if (!status) return "#3B82F6";
  return INVOICE_COLORS[status.toUpperCase()] ?? "#3B82F6";
}

function statusBarColors(status: InvoiceStatus | null | undefined): { bg: string; border: string } {
  if (!status) return STATUS_BAR_COLORS.GENERATED;
  return STATUS_BAR_COLORS[status.toUpperCase()] ?? STATUS_BAR_COLORS.GENERATED;
}

const SERIES_CONFIG = [
  {
    id: "CLIENT_PREMIUM",
    label: "Client Premium",
    color: "#1E3A8A",
    key: "clientPremium" as const,
    illustratedKey: "clientPremiumIllustrated" as const,
    invoiceStatusKey: "clientPremiumInvoiceStatus" as const,
  },
  {
    id: "LENDER_PREMIUM",
    label: "Lender Premium",
    color: "#1E3A8A99",
    key: "lenderPremium" as const,
    illustratedKey: "lenderPremiumIllustrated" as const,
    invoiceStatusKey: "lenderPremiumInvoiceStatus" as const,
  },
  {
    id: "INTEREST_PAYMENT",
    label: "Interest Payment",
    color: "#1E3A8A73",
    key: "clientInterestPayment" as const,
    illustratedKey: "clientInterestPaymentIllustrated" as const,
    invoiceStatusKey: "interestPaymentInvoiceStatus" as const,
  },
  {
    id: "PLAN_FEE",
    label: "Plan Fee",
    color: "#1E3A8A4D",
    key: "clientPlanFee" as const,
    illustratedKey: "clientPlanFeeIllustrated" as const,
    invoiceStatusKey: "planFeeInvoiceStatus" as const,
  },
  {
    id: "ORIGINATION_FEE",
    label: "Origination Fee",
    color: "#1E3A8A26",
    key: "clientOriginationFees" as const,
    illustratedKey: null,
    invoiceStatusKey: null,
  },
] as const;

const SERIES_ICONS: Record<string, React.ComponentType<any>> = {
  CLIENT_PREMIUM: HexagonTick,
  LENDER_PREMIUM: FundingIcon,
  INTEREST_PAYMENT: WarningHex,
  PLAN_FEE: HexagonTick,
  ORIGINATION_FEE: HexagonTick,
};

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FundingChartRow {
  planYear: number;
  year: number;
  insuredAge: number;
  clientPremium: number;
  clientPlanFee: number;
  clientInterestPayment: number;
  clientOriginationFees: number;
  lenderPremium: number;
  lenderOriginationFees: number;
  totalPolicyPremium: number;
  clientPremiumIllustrated: number;
  lenderPremiumIllustrated: number;
  clientInterestPaymentIllustrated: number;
  clientPlanFeeIllustrated: number;
  trueIQIndex?: number;
  combinedTrueIQIndex?: number;
  clientPremiumInvoiceStatus?: InvoiceStatus;
  interestPaymentInvoiceStatus?: InvoiceStatus;
  lenderPremiumInvoiceStatus?: InvoiceStatus;
  planFeeInvoiceStatus?: InvoiceStatus;
}

interface TooltipState {
  row: FundingChartRow;
  x: number;
  y: number;
}

interface FundingChartProps {
  data: FundingChartRow[];
  activeFilters: Set<string>;
  isDark: boolean;
  selectedYear: number;
  loading?: boolean;
  showProjections: boolean;
  isContact: boolean;
  showIndex?: boolean;
  selectedChartFilter?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function getPaymentStatus(
  actual: number,
  _illustrated: number,
  calendarYear: number,
): PaymentStatus {
  if (actual > 0) return "paid";
  return calendarYear < CURRENT_YEAR ? "overdue" : "due";
}

function StatusIcon({ status, size = 14 }: { status: string; size?: number }) {
  const upper = (status ?? "").toUpperCase();
  const color = upper === "PAID" ? "#22C55E" : upper === "OVERDUE" ? "#F97316" : "#3B82F6";
  const isPaid = upper === "PAID";
  const isOverdue = upper === "OVERDUE";

  if (isPaid) {
    return (
      <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="7" cy="7" r="7" fill={color} />
        <path
          d="M4 7L6 9.5L10 5"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (isOverdue) {
    return (
      <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
        <circle cx="7" cy="7" r="7" fill={color} />
        <path d="M7 4V7.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="7" cy="9.5" r="0.8" fill="white" />
      </svg>
    );
  }

  // GENERATED / due → blue hexagon with $
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <path d="M7 0.5L13.2 3.75V10.25L7 13.5L0.8 10.25V3.75L7 0.5Z" fill={color} />
      <text
        x="7"
        y="9.5"
        textAnchor="middle"
        fontSize="5.5"
        fill="white"
        fontWeight="800"
        fontFamily="sans-serif"
      >
        $
      </text>
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

function FundingChart({
  data,
  activeFilters,
  isDark,
  selectedYear,
  loading,
  showProjections,
  isContact,
  showIndex,
  selectedChartFilter = "insuredAge",
}: FundingChartProps) {
  const navigate = useNavigate();
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const echartsRef = useRef<any>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const zoomStateRef = useRef<{ start: number; end: number } | null>(null);

  const scheduleHide = () => {
    hideTimerRef.current = setTimeout(() => setTooltip(null), 120);
  };
  const cancelHide = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  };

  const nextYear = selectedYear + 1;

  // ── ECharts option ───────────────────────────────────────────────────────────

  const option = useMemo(() => {
    const textColor = isDark ? "#e2e8f0" : "#334155";
    const gridColor = isDark ? "#3A3A50" : "#e2e8f0";

    const nextYr = selectedYear + 1;
    const lightBlue = isDark ? "rgba(96,165,250,0.35)" : "rgba(147,197,253,0.65)";

    const activeCfgs = SERIES_CONFIG.filter((s) => activeFilters.has(s.id));

    // ── Stacked data series (one per active component) ───────────────────────
    const dataSeries = activeCfgs.map((cfg) => ({
      id: `data_${cfg.id}`,
      name: cfg.label,
      type: "bar",
      stack: "total",
      barMaxWidth: 80,
      barCategoryGap: "20%",
      data: data.map((row) => {
        const isCompleted = row.year <= selectedYear;
        const isNextYr = row.year === nextYr;
        const isProjection = row.year > selectedYear;
        const gapColor = isDark ? "#2A2A3D" : "#ffffff";

        if (isProjection && !showProjections) {
          return { value: 0, itemStyle: { color: "transparent" } };
        }

        const illustratedVal = cfg.illustratedKey
          ? row[cfg.illustratedKey] ?? 0
          : row[cfg.key] ?? 0;

        const invStatus = cfg.invoiceStatusKey ? row[cfg.invoiceStatusKey] : undefined;

        // ── Completed: per-series color ──────────────────────────────────────
        if (isCompleted) {
          return {
            value: row[cfg.key] ?? 0,
            itemStyle: { color: cfg.color, borderWidth: 0.5, borderColor: gapColor },
          };
        }

        // ── Immediate next year ──────────────────────────────────────────────
        if (isNextYr) {
          const actual = row[cfg.key] ?? 0;
          const resolvedStatus =
            invStatus ??
            (cfg.illustratedKey
              ? getPaymentStatus(actual, illustratedVal, row.planYear) === "paid"
                ? "PAID"
                : getPaymentStatus(actual, illustratedVal, row.planYear) === "overdue"
                ? "OVERDUE"
                : "GENERATED"
              : "GENERATED");
          const { bg, border } = statusBarColors(resolvedStatus);
          return {
            value: illustratedVal,
            // Default: projection look; outline series adds the dashed blue border
            itemStyle: {
              color: lightBlue,
              borderRadius: 0,
              borderWidth: 0.5,
              borderColor: gapColor,
            },
            // Hover: breakdown color revealed via ECharts emphasis (no React re-render)
            emphasis: {
              itemStyle: {
                color: bg,
                borderWidth: 1.5,
                borderColor: border,
                borderType: "dashed" as const,
                opacity: 1,
              },
            },
          };
        }

        // ── Future years (2+ ahead): seamless light-blue fill ────────────────
        return {
          value: illustratedVal,
          itemStyle: { color: lightBlue, borderRadius: 0, borderWidth: 0.5, borderColor: gapColor },
        };
      }),
    }));

    // ── Outline series: dashed border for unselected future projection bars ──
    const outlineSeries = {
      id: "__outline__",
      name: "__outline__",
      type: "bar",
      barMaxWidth: 80,
      barCategoryGap: "20%",
      barGap: "-100%",
      silent: true,
      emphasis: { disabled: true },
      data: data.map((row) => {
        const isNextYrRow = row.year === nextYr;
        const isFuture = row.year > nextYr;
        const showDash = (isFuture || isNextYrRow) && showProjections;
        if (!showDash) {
          return { value: 0, itemStyle: { color: "transparent", borderWidth: 0 } };
        }
        const total = activeCfgs.reduce((sum, cfg) => {
          const v = cfg.illustratedKey ? row[cfg.illustratedKey] ?? 0 : row[cfg.key] ?? 0;
          return sum + v;
        }, 0);
        return {
          value: total,
          itemStyle: {
            color: "transparent",
            borderColor: isDark ? "rgba(96,165,250,0.7)" : "#3B82F6",
            borderType: "dashed",
            borderWidth: 1.5,
          },
        };
      }),
    };

    // ── Next-year funding icon series ────────────────────────────────────────────
    const fundingIconColor = showIndex ? "#BDBDBD" : "#004781";
    const fundingSvgStr = renderToStaticMarkup(<FundingIcon fill={fundingIconColor} />).replace(
      /currentColor/g,
      fundingIconColor,
    );
    const fundingDataUrl = `data:image/svg+xml,${encodeURIComponent(fundingSvgStr)}`;

    const nextYrIconSeries = {
      id: "__nextyr__",
      name: "__nextyr__",
      type: "bar",
      barMaxWidth: 80,
      barCategoryGap: "20%",
      barGap: "-100%",
      silent: true,
      emphasis: { disabled: true },
      data: data.map((row) => {
        const isNextYr = row.year === nextYr;
        if (!isNextYr || !showProjections) {
          return {
            value: 0,
            label: { show: false },
            itemStyle: { color: "transparent", borderWidth: 0 },
          };
        }
        const total = activeCfgs.reduce((sum, cfg) => {
          const v = cfg.illustratedKey ? row[cfg.illustratedKey] ?? 0 : row[cfg.key] ?? 0;
          return sum + v;
        }, 0);
        return {
          value: total,
          label: { show: true },
          itemStyle: { color: "transparent", borderWidth: 0 },
        };
      }),
      label: {
        show: true,
        position: "top" as const,
        distance: 6,
        align: "center" as const,
        formatter: () => "{icon|}",
        rich: {
          icon: {
            width: 45,
            height: 45,
            backgroundColor: { image: fundingDataUrl },
            verticalAlign: "middle",
          },
        },
      },
    };

    // ── TrueIQ index label series: badge on top of each bar when showIndex=true ─
    const iconColor = "#004781";
    const trueIqSvgStr = renderToStaticMarkup(
      <TrueIqIcon fill={iconColor} width={20} height={18} />,
    ).replace(/currentColor/g, iconColor);
    const trueIqDataUrl = `data:image/svg+xml,${encodeURIComponent(trueIqSvgStr)}`;

    const indexSeries = {
      id: "__index__",
      name: "__index__",
      type: "bar",
      barMaxWidth: 80,
      barCategoryGap: "20%",
      barGap: "-100%",
      silent: true,
      emphasis: { disabled: true },
      data: data.map((row) => {
        const isProjection = row.year > selectedYear;
        if (isProjection && !showProjections) {
          return {
            value: 0,
            label: { show: false },
            itemStyle: { color: "transparent", borderWidth: 0 },
          };
        }
        const total = activeCfgs.reduce((sum, cfg) => {
          const v =
            isProjection && cfg.illustratedKey ? row[cfg.illustratedKey] ?? 0 : row[cfg.key] ?? 0;
          return sum + v;
        }, 0);
        return {
          value: total,
          label: { show: showIndex && row.combinedTrueIQIndex != null && row.year <= selectedYear },
          itemStyle: { color: "transparent", borderWidth: 0 },
        };
      }),
      label: {
        show: showIndex,
        position: "top" as const,
        distance: 4,
        align: "center" as const,
        formatter: (params: any) => {
          const row = data[params.dataIndex];
          const val = row?.combinedTrueIQIndex;
          if (val == null) return "";
          return `{icon| }{gap|}{val|${val.toFixed(1)}}`;
        },
        rich: {
          icon: {
            width: 20,
            height: 18,
            backgroundColor: { image: trueIqDataUrl },
            verticalAlign: "middle",
          },
          gap: {
            width: 2,
          },
          val: {
            fontSize: 16,
            color: "#004781",
            fontWeight: "600",
            fontFamily: "Conthrax Semi-Bold, sans-serif",
            verticalAlign: "middle",
          },
        },
      },
    };

    const useInitialWindow = !zoomStateRef.current;
    const initialEndIndex = useInitialWindow
      ? Math.min(INITIAL_VISIBLE_RECORDS - 1, Math.max(data.length - 1, 0))
      : undefined;

    return {
      animation: true,
      tooltip: { show: false },
      legend: { show: false },
      grid: { left: 56, right: 12, top: showIndex ? 55 : 55, bottom: 56 },
      dataZoom: [
        {
          type: "slider",
          show: true,
          start: useInitialWindow ? undefined : zoomStateRef.current?.start ?? 0,
          end: useInitialWindow
            ? undefined
            : zoomStateRef.current?.end ??
              Math.min(100, (INITIAL_VISIBLE_RECORDS / (data.length || 1)) * 100),
          startValue: useInitialWindow ? 0 : undefined,
          endValue: useInitialWindow ? initialEndIndex : undefined,
          minValueSpan: 1,
          maxValueSpan: data.length || 1,
          height: 24,
          bottom: 4,
          borderColor: "transparent",
          fillerColor: isDark ? "rgba(167,183,204,0.3)" : "rgba(167,183,204,0.4)",
          handleStyle: {
            color: isDark ? "#ffffff" : "#334155",
            borderColor: isDark ? "#4A4A60" : "#E5E7EB",
          },
          textStyle: { color: textColor, fontSize: 10 },
          showDetail: true,
          showDataShadow: true,
          realtime: true,
          filterMode: "filter",
          xAxisIndex: 0,
        },
      ],
      xAxis: {
        type: "category",
        data: data.map((r) => {
          if (selectedChartFilter === "year") return `${r.planYear}`;
          if (selectedChartFilter === "planYear") return `Yr ${r.year}`;
          return `A${r.insuredAge}`;
        }),
        axisLine: { lineStyle: { color: gridColor } },
        axisTick: { show: false },
        axisLabel: { color: textColor, fontSize: 11 },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          color: textColor,
          fontSize: 11,
          formatter: (v: number) => (v >= 1000 ? `$${v / 1000}K` : `$${v}`),
        },
        splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
      },
      series: [...dataSeries, outlineSeries, indexSeries, nextYrIconSeries],
    };
  }, [data, activeFilters, isDark, selectedYear, showProjections, showIndex, selectedChartFilter]);

  // ── Mouse event handlers ─────────────────────────────────────────────────────

  const onMouseOver = (params: any) => {
    if (
      params.seriesName === "__outline__" ||
      params.seriesName === "__index__" ||
      params.seriesName === "__nextyr__"
    ) {
      return;
    }
    const row = data[params.dataIndex];
    if (!row) return;
    if (row.year > nextYear) return;
    const evt = params.event?.event as MouseEvent | undefined;
    if (!evt) return;

    // Highlight all series at this dataIndex simultaneously so every stacked
    // segment shows its emphasis.itemStyle at once (no React re-render needed)
    if (row.year === nextYear) {
      const chart = echartsRef.current?.getEchartsInstance?.();
      chart?.dispatchAction({ type: "highlight", dataIndex: params.dataIndex });
    }

    setTooltip({ row, x: evt.clientX, y: evt.clientY });
  };

  const onMouseOut = (params: any) => {
    if (
      params.seriesName === "__outline__" ||
      params.seriesName === "__index__" ||
      params.seriesName === "__nextyr__"
    )
      // eslint-disable-next-line
      return;
    const row = data[params.dataIndex];
    if (row?.year === nextYear) {
      const chart = echartsRef.current?.getEchartsInstance?.();
      chart?.dispatchAction({ type: "downplay", dataIndex: params.dataIndex });
    }
  };

  // ── Tooltip state ────────────────────────────────────────────────────────────

  const tooltipRow = tooltip?.row ?? null;
  const isNextYear = tooltipRow?.year === nextYear;

  // ── Theme tokens ─────────────────────────────────────────────────────────────

  const borderColor = isDark ? "#3A3A50" : "#E5E7EB";
  const panelBg = isDark ? "#1E2030" : "#FFFFFF";
  const textSub = isDark ? "#94A3B8" : "#64748B";

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* ── Chart ─────────────────────────────────────────────────────────── */}
      <div onMouseLeave={scheduleHide}>
        <ReactECharts
          ref={echartsRef}
          option={option}
          style={{ height: 300, cursor: "default" }}
          notMerge
          lazyUpdate
          showLoading={loading}
          loadingOption={{
            text: "",
            color: PRIMARY,
            maskColor: isDark ? "rgba(30,32,48,0.8)" : "rgba(255,255,255,0.8)",
          }}
          onEvents={{
            mouseover: onMouseOver,
            mouseout: onMouseOut,
            dataZoom: (params: any) => {
              zoomStateRef.current = { start: params.start, end: params.end };
            },
          }}
        />
      </div>

      {tooltip &&
        tooltipRow &&
        createPortal(
          <div
            className="fixed rounded-xl border shadow-2xl"
            style={{
              left: Math.min(tooltip.x + 14, window.innerWidth - 260),
              top: Math.max(tooltip.y - 24, 8),
              background: panelBg,
              borderColor,
              minWidth: 230,
              zIndex: 99999,
            }}
            onMouseEnter={cancelHide}
            onMouseLeave={scheduleHide}
          >
            {/* Items */}
            <div className="px-3 pt-2 pb-4 flex flex-col gap-2">
              {SERIES_CONFIG.map((cfg) => {
                if (!activeFilters.has(cfg.id)) return null;
                if (cfg.id === "ORIGINATION_FEE") return null;

                const actual = tooltipRow[cfg.key];
                const illustrated = cfg.illustratedKey ? tooltipRow[cfg.illustratedKey] : actual;
                const invStatus = cfg.invoiceStatusKey
                  ? tooltipRow[cfg.invoiceStatusKey]
                  : undefined;

                let statusNode: React.ReactNode;
                let labelColor = textSub;
                let rowBg: string | undefined;

                if (invStatus) {
                  // Invoice status from API — applies to any year that has it
                  const c = invoiceColor(invStatus);
                  labelColor = c;
                  rowBg = `${c}20`;
                  statusNode = <StatusIcon status={invStatus} size={15} />;
                } else if (actual > 0) {
                  // No invoice status but actual value is present → paid
                  labelColor = PAYMENT_STATUS.paid.color;
                  rowBg = `${PAYMENT_STATUS.paid.color}20`;
                  statusNode = <StatusIcon status="PAID" size={15} />;
                } else if (isNextYear && cfg.illustratedKey) {
                  // Fallback computation for next year when no invoice status and no actual
                  const status = getPaymentStatus(actual, illustrated, tooltipRow.planYear);
                  labelColor = PAYMENT_STATUS[status].color;
                  rowBg = `${PAYMENT_STATUS[status].color}20`;
                  statusNode = <StatusIcon status={status} size={15} />;
                } else {
                  const Icon = SERIES_ICONS[cfg.id];
                  statusNode = (
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: cfg.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        padding: "2px",
                      }}
                    >
                      <Icon fill="#fff" width={18} height={18} />
                    </div>
                  );
                }

                // const displayVal = isNextYear && cfg.illustratedKey ? illustrated : actual;

                return (
                  <div
                    key={cfg.id}
                    className="flex items-center justify-between gap-3"
                    style={
                      rowBg
                        ? {
                            borderRadius: 6,
                            padding: "3px 6px",
                            margin: "0 -6px",
                          }
                        : undefined
                    }
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {statusNode}
                      <span className="text-[14px] font-normal" style={{ color: labelColor }}>
                        {cfg.label}
                      </span>
                    </div>
                  </div>
                );
              })}

              {isNextYear && (
                <button
                  type="button"
                  disabled={!isContact}
                  className="w-full mt-0.5 py-1.5 rounded-lg text-[14px] font-bold transition-all border"
                  style={{
                    background: "transparent",
                    color: isContact ? "#2A7FFF" : textSub,
                    border: `1.5px solid ${isContact ? "#2A7FFF" : borderColor}`,
                    cursor: isContact ? "pointer" : "not-allowed",
                    opacity: isContact ? 1 : 0.5,
                  }}
                  onClick={() => navigate("/plan-summary/plan?tab=plan")}
                >
                  Pay Now
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export default FundingChart;
